import * as React from 'react'
import { Link } from '@tanstack/react-router'
import { Gauge } from 'lucide-react'
import { Tooltip } from '@/components/ui/tooltip'
import { Sparkline, type ChartPoint } from '@/components/system/charts'
import {
  latestSelf,
  selfService,
  systemHealth,
  useSelfMetricsHistory,
  useSystemMetrics,
  type SystemTone,
} from '@/hooks/use-system-metrics'
import { cn } from '@/lib/cn'

/**
 * The rail's system chip — pinned at the bottom, above the footer controls.
 *
 * A compact version of `/system` rather than a link to it: the numbers are on
 * screen on every route, and clicking opens the full view. That is the whole
 * point of putting it here — an operator watching a hunt should not have to
 * navigate away from the customer list to find out that the box is struggling,
 * because navigating away is exactly what they will not do while something is
 * running.
 *
 * It sits OUTSIDE the scrolling nav so it cannot be scrolled out of view, and
 * outside the footer so it does not compete with the connection dot — that dot
 * answers "is my stream up", this answers "is the server healthy", and the two
 * genuinely come apart: a perfectly connected console can be attached to a
 * process whose event loop is stalled.
 */

/**
 * Green for healthy, and green specifically because this console reserves it
 * for "this is running" — the same hue the live pill uses. Indigo would read as
 * "selected", which is what the accent means everywhere else in the rail.
 */
const DOT_TONE: Record<SystemTone, string> = {
  ok: 'bg-live',
  warn: 'bg-warn',
  danger: 'bg-danger',
  neutral: 'bg-neutral',
}

const TEXT_TONE: Record<SystemTone, string> = {
  ok: 'text-live',
  warn: 'text-warn',
  danger: 'text-danger',
  neutral: 'text-muted',
}

/*
 * Mirrors ITEM_ACTIVE / ITEM_INACTIVE in rail.tsx.
 *
 * Duplicated rather than imported because rail.tsx imports THIS module, and a
 * cycle for two class strings is a worse trade than two class strings. The
 * shape is the same on purpose: a soft accent tint plus the 2px left bar, so
 * the chip reads as part of the navigation and not as a widget parked near it.
 */
const CHIP_INACTIVE = 'text-muted hover:bg-s3 hover:text-fg'

const CHIP_ACTIVE =
  'bg-accent-soft text-accent ' +
  "before:absolute before:top-1/2 before:left-0 before:h-5 before:w-[2px] " +
  "before:-translate-y-1/2 before:rounded-full before:bg-accent before:content-['']"

function formatVcpu(value: number): string {
  return value >= 10 ? value.toFixed(0) : value.toFixed(2)
}

function formatGb(value: number): string {
  return value >= 10 ? value.toFixed(1) : value.toFixed(2)
}

export function RailSystemChip({
  collapsed,
  touch,
  onNavigate,
}: {
  collapsed: boolean
  touch: boolean
  onNavigate?: (() => void) | undefined
}) {
  const query = useSystemMetrics()
  const history = useSelfMetricsHistory()

  const snapshot = query.data
  const self = latestSelf(history, snapshot)
  const health = systemHealth(self, selfService(snapshot), snapshot?.railway.error ?? null)

  const cpuPoints = React.useMemo<ChartPoint[]>(
    () => history.map((s) => ({ x: s.at, y: s.cpu })),
    [history]
  )

  const summary = self
    ? `${formatVcpu(self.cpu)} vCPU · ${formatGb(self.rssGb)} GB · ${Math.round(self.eventLoopDelayMs)}ms loop`
    : 'No sample yet'

  return (
    <div className={cn('flex-none border-t border-line', collapsed ? 'p-1.5' : 'p-2')}>
      <Tooltip content={`System · ${health.label} — ${health.detail}`} side="right">
        <Link
          to="/system"
          activeOptions={{ exact: true, includeSearch: false }}
          onClick={onNavigate}
          aria-label={`System — ${health.label}. ${summary}.`}
          className={cn(
            'group relative flex w-full rounded-md transition-colors duration-100',
            collapsed
              ? cn('items-center justify-center', touch ? 'h-11' : 'h-9')
              : 'flex-col gap-1.5 px-2.5 py-2'
          )}
          activeProps={{ className: CHIP_ACTIVE }}
          inactiveProps={{ className: CHIP_INACTIVE }}
        >
          {collapsed ? (
            <span className="relative flex items-center justify-center">
              <Gauge aria-hidden="true" className="size-4" />
              {/* Anchored to the icon rather than placed beside it: in a 60px
                  column there is no beside. */}
              <span
                aria-hidden="true"
                className={cn(
                  'absolute -top-0.5 -right-1 size-[7px] rounded-full',
                  // The ring in the surface colour keeps the dot legible where
                  // it overlaps the glyph.
                  'ring-2 ring-s1',
                  DOT_TONE[health.tone]
                )}
              />
            </span>
          ) : (
            <>
              <span className="flex w-full items-center gap-2">
                <Gauge aria-hidden="true" className="size-4 flex-none" />
                <span className="min-w-0 flex-1 truncate text-[14.5px] font-medium">System</span>
                <span
                  aria-hidden="true"
                  className={cn('size-[6px] flex-none rounded-full', DOT_TONE[health.tone])}
                />
                <span className={cn('flex-none text-[11.5px] font-medium', TEXT_TONE[health.tone])}>
                  {health.label}
                </span>
              </span>

              {/* The compact analytical readout. Monospace and tabular so the
                  numbers do not jitter sideways twice a second as digits
                  change — a value that dances is a value nobody can read. */}
              <span className="w-full font-mono text-[11px] text-faint tabular">
                {self ? (
                  <>
                    {formatVcpu(self.cpu)} vCPU · {formatGb(self.rssGb)} GB ·{' '}
                    {Math.round(self.eventLoopDelayMs)}ms
                  </>
                ) : (
                  'waiting for first sample'
                )}
              </span>

              {cpuPoints.length > 1 ? (
                <Sparkline points={cpuPoints} height={16} className="w-full" />
              ) : null}
            </>
          )}
        </Link>
      </Tooltip>
    </div>
  )
}
